import {
  type FeatureFlag,
  featureEnabled,
} from '@sparklayer-io/spark-dash-core/constants/features';
import { isShopify } from '@sparklayer-io/spark-dash-core/constants/platform';
import type { Env, Platform } from '@sparklayer-io/spark-dash-core/context/env-context';
import type { ComponentType } from 'react';
import type { HostUser } from '@/lib/session';

export type SubNavItem = {
  href: string;
  title: string;
  feature?: FeatureFlag;
  notFeature?: FeatureFlag;
  hide?: boolean;
  hidden?: boolean;
};

export type NavItem = SubNavItem & {
  icon?: ComponentType<{ className?: string }>;
  subNav?: SubNavItem[];
};

// The parts of the TanStack router the sidebar drives directly - hrefs below are already full
// paths, so navigation bypasses the site-prefixing `navigationHandler` and calls the router the
// same way it does internally.
export type RuntimeNavigate = (opts: {
  to: string;
  search?: unknown;
  hash?: string;
  replace?: boolean;
}) => Promise<void> | void;

export const EXPANDED_NAV_STORAGE_KEY = 'sidebar-expanded-nav';

export function hasPermission(user: HostUser, permission: keyof HostUser['permissions']): boolean {
  return !!user.permissions[permission];
}

export function acronymOf(name: string): string {
  return (name.match(/\b\w/g)?.join('') ?? '').toUpperCase().slice(0, 2);
}

/** `isPlatform('shopify')` matches Shopify Plus too, since the dashboard almost always wants the
 * whole Shopify family. */
export function isPlatform(env: Env, platform: string): boolean {
  return platform === 'shopify'
    ? isShopify(env.connected_platform)
    : env.connected_platform === platform;
}

export function isNavItemActive(pathname: string, navItem: SubNavItem): boolean {
  const hrefPath = navItem.href.split('?')[0];

  if (pathname === hrefPath) {
    return true;
  }

  // For paths ending with /, only match exactly (e.g., Home)
  if (hrefPath.endsWith('/')) {
    return pathname === hrefPath.slice(0, -1);
  }

  // Check if current path equals or starts with the nav item's path segment
  // This handles cases like /forms matching /forms/builder/xyz
  return pathname.startsWith(`${hrefPath}/`);
}

export function isSubNavItemActive(
  pathname: string,
  subNavItem: SubNavItem,
  siblings: SubNavItem[],
): boolean {
  if (!isNavItemActive(pathname, subNavItem)) {
    return false;
  }
  // When multiple siblings prefix-match (e.g. /forms and /forms/tables on
  // /forms/tables), only the most specific one wins.
  const hrefPath = subNavItem.href.split('?')[0];
  return !siblings.some((sibling) => {
    if (sibling === subNavItem || sibling.hidden) return false;
    const siblingHref = sibling.href.split('?')[0];
    return siblingHref.length > hrefPath.length && isNavItemActive(pathname, sibling);
  });
}

export function filterNavItems<T extends SubNavItem>(
  items: T[],
  siteId: string,
  platform?: Platform,
): T[] {
  return items.filter((item) => {
    if (item.feature !== undefined && !featureEnabled(item.feature, siteId, platform)) return false;
    if (item.notFeature !== undefined && featureEnabled(item.notFeature, siteId, platform))
      return false;
    return !item.hide;
  });
}
